<template>
  <div>
    <v-dialog v-model="dialog" eager persistent scrollable max-width="680">
      <v-form ref="InternRequestForm" @submit.prevent>
        <v-card class="dialog-card">
          <!-- Header -->
          <div class="dialog-header">
            <div class="dialog-header-icon">
              <v-icon size="20" color="#16a34a">mdi-account-plus</v-icon>
            </div>
            <div>
              <div class="dialog-title">{{ action }} Intern Request</div>
              <div class="dialog-subtitle">
                Find the perfect interns for your organization
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              @click="closeD()"
              class="close-btn"
            />
          </div>

          <v-divider></v-divider>

          <v-card-text class="dialog-body">
            <!-- Step 1: Number of Interns -->
            <div class="form-section">
              <div class="section-label">
                <span class="section-num">1</span>
                How many interns do you need?
              </div>
              <v-text-field
                :rules="[
                  formRules.required,
                  formRules.onlyNumberNoNegative,
                  formRules.noDecimal,
                ]"
                type="number"
                v-model="num_interns"
                variant="outlined"
                density="compact"
                placeholder="e.g. 5"
                hide-details="auto"
                class="field-input"
                color="#16a34a"
              />
            </div>

            <!-- Step 2: Preferred Programs -->
            <div class="form-section">
              <div class="d-flex justify-space-between">
                <div>
                  <div class="section-label">
                    <span class="section-num">2</span>
                    Preferred Programs
                  </div>
                  <div class="programs-grid">
                    <div
                      v-for="i in programsList"
                      :key="i.id"
                      class="program-chip"
                      :class="{ active: programSelected.includes(i.id) }"
                      @click="toggleProgram(i.id)"
                    >
                      <v-icon
                        size="14"
                        class="chip-check"
                        v-if="programSelected.includes(i.id)"
                        >mdi-check</v-icon
                      >
                      <span>{{ i.abbr }}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <div class="section-label">Select Interns Batch</div>
                  <v-autocomplete
                    v-model="batch_selected"
                    @update:modelValue="loadInternsByBatch"
                    :items="batchList"
                    item-value="id"
                    :item-title="(item) => `${item.name}  (${item.year})`"
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                    class="field-input mt-2"
                    :color="$vuetify.theme.themes.light.inputColor"
                  ></v-autocomplete>
                </div>
              </div>
            </div>

            <!-- Step 3: Request Mode Toggle -->
            <div class="form-section" v-if="programSelected.length > 0">
              <div class="section-label">
                <span class="section-num">3</span>
                How would you like to select interns?
              </div>
              <div class="mode-toggle">
                <div
                  class="mode-option"
                  :class="{ active: requestMode === 'skills' }"
                  @click="requestMode = 'skills'"
                >
                  <div
                    class="mode-icon-wrap"
                    :class="{ active: requestMode === 'skills' }"
                  >
                    <v-icon size="18">mdi-lightbulb-outline</v-icon>
                  </div>
                  <div class="mode-text">
                    <div class="mode-title">By Skills</div>
                    <div class="mode-desc">
                      Set required skills, system matches interns
                    </div>
                  </div>
                </div>
                <div
                  class="mode-option"
                  :class="{ active: requestMode === 'specific' }"
                  @click="requestMode = 'specific'"
                >
                  <div
                    class="mode-icon-wrap"
                    :class="{ active: requestMode === 'specific' }"
                  >
                    <v-icon size="18">mdi-account-search-outline</v-icon>
                  </div>
                  <div class="mode-text">
                    <div class="mode-title">Specific Interns</div>
                    <div class="mode-desc">
                      Search and hand-pick interns by name
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 4a: Skills selection -->
            <div
              class="form-section"
              v-if="
                requestMode === 'skills' &&
                skillRequired &&
                skillsList.length > 0
              "
            >
              <div class="section-label">
                <span class="section-num">4</span>
                Required Skills
                <span class="section-badge"
                  >{{ selectedSkills.length }} selected</span
                >
              </div>
              <div class="skills-grid">
                <div
                  v-for="i in skillsList"
                  :key="i.id"
                  class="skill-chip"
                  :class="{ active: selectedSkills.includes(i.title) }"
                  @click="toggleSkill(i.title)"
                >
                  <v-icon
                    size="13"
                    v-if="selectedSkills.includes(i.title)"
                    class="skill-check"
                    >mdi-check</v-icon
                  >
                  <span>{{ i.title }}</span>
                </div>
              </div>
            </div>

            <!-- Step 4b: Specific student selection -->
            <div
              class="form-section"
              v-if="requestMode === 'specific' && studentList.length > 0"
            >
              <div class="section-label">
                <span class="section-num">4</span>
                Select Interns
                <span class="section-badge"
                  >{{ selectedStudents.length }} selected</span
                >
              </div>
              <v-autocomplete
                density="compact"
                placeholder="Search by name..."
                v-model="selectedStudents"
                item-value="id"
                :item-title="
                  (item) =>
                    item.fname +
                    ' ' +
                    item.lname +
                    ' (' +
                    getProgramAbbr(item.program_id) +
                    ')'
                "
                color="#16a34a"
                variant="outlined"
                multiple
                closable-chips
                chips
                :items="studentList"
                hide-details="auto"
                class="field-input"
                prepend-inner-icon="mdi-magnify"
              >
                <template v-slot:chip="{ props, item }">
                  <v-chip
                    v-bind="props"
                    color="#16a34a"
                    variant="tonal"
                    size="small"
                    class="student-chip"
                  >
                    {{ item.raw.fname }} {{ item.raw.lname }} ({{
                      getStudentProgram(item.raw.id)
                    }})
                  </v-chip>
                </template>
              </v-autocomplete>

              <!-- Selected students preview -->
              <!-- <div class="selected-preview" v-if="selectedStudents.length > 0">
                <div
                  v-for="id in selectedStudents"
                  :key="id"
                  class="selected-student-row"
                >
                  <div class="student-avatar">
                    {{ getStudentInitials(id) }}
                  </div>
                  <div class="student-info">
                    <span class="student-name">{{ getStudentName(id) }}</span>
                    <span class="student-prog">{{
                      getStudentProgram(id)
                    }}</span>
                  </div>
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    class="remove-btn"
                    @click="removeStudent(id)"
                  >
                    <v-icon size="14">mdi-close</v-icon>
                  </v-btn>
                </div>
              </div> -->
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <!-- Footer Actions -->
          <div class="dialog-footer" v-if="action != 'View'">
            <v-btn class="footer-cancel-btn" elevation="0" @click="closeD()">
              Cancel
            </v-btn>
            <v-btn
              class="footer-submit-btn"
              elevation="0"
              :disabled="btnLoading"
              :loading="btnLoading"
              @click="action == 'Add' ? save() : update()"
            >
              <v-icon start size="16">mdi-check-circle</v-icon>
              {{ action == "Add" ? "Submit Request" : "Update Request" }}
            </v-btn>
          </div>
        </v-card>
      </v-form>
    </v-dialog>

    <fade-away-message-component
      displayType="default"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    ></fade-away-message-component>
  </div>
</template>

<script>
import eventBus from "@/eventBus";
export default {
  props: {
    data: Object,
    action: String,
  },
  data() {
    return {
      dialog: false,
      btnLoading: false,
      name: null,
      programsList: [],
      programSelected: [],
      studentList: [],
      num_interns: null,
      skillRequired: false,
      skillsList: [],
      selectedStudents: [],
      selectedSkills: [],
      requestMode: "skills", // 'skills' | 'specific'
      fadeAwayMessage: {
        show: false,
        type: "success",
        header: "Successfully Added!",
        message: "",
        top: 10,
      },
      batchList: [],
      batch_selected: null,
    };
  },
  watch: {
    data: {
      handler(data) {
        this.dialog = true;

        this.getBatchList();
        this.initialize();
        this.$refs.InternRequestForm.resetValidation();
        if (data.id) {
          console.log(data);
          this.selectedStudents = data.student_requested;
          this.selectedSkills = data.skills;
          this.programSelected = data.programs;
          this.num_interns = data.num_interns;
          this.requestMode =
            data.student_requested?.length > 0 ? "specific" : "skills";
          this.batch_selected = data.student_batch_id
            ? data.student_batch_id.toString()
            : null;
          this.loadSkills();
        } else {
          this.selectedStudents = [];
          this.programSelected = [];
          this.skillsList = [];
          this.num_interns = null;
          this.selectedSkills = [];
          this.skillRequired = false;
          this.requestMode = "skills";
        }
      },
      deep: true,
    },
  },
  methods: {
    toggleProgram(id) {
      const idx = this.programSelected.indexOf(id);
      if (idx === -1) this.programSelected.push(id);
      else this.programSelected.splice(idx, 1);
      this.loadSkills();
    },

    toggleSkill(title) {
      const idx = this.selectedSkills.indexOf(title);
      if (idx === -1) this.selectedSkills.push(title);
      else this.selectedSkills.splice(idx, 1);
    },

    loadSkills() {
      let progs = JSON.stringify(this.programSelected);
      if (this.programSelected.length > 0) {
        this.axiosCall(
          "/skill-assessement/getSkillCategoryByProgramAndStudentBatch/" +
            this.batch_selected +
            "/" +
            progs,
          "GET",
        ).then((res) => {
          if (res.data) {
            this.skillsList = res.data.data;
            this.skillRequired = res.data.isRequired;
            this.studentList = res.data.students;
            const validIds = new Set(this.studentList.map((s) => s.id));
            this.selectedStudents = this.selectedStudents.filter((id) =>
              validIds.has(id),
            );
          }
        });
      } else {
        this.skillsList = [];
        this.skillRequired = false;
        this.studentList = [];
        this.selectedStudents = [];
      }
    },

    getBatchList() {
      this.axiosCall("/student/get-institute-batch-list", "GET").then((res) => {
        console.log("BATCH DATA", res.data);
        this.batchList = res.data;
        if (this.action == "Add") {
          this.batch_selected = this.batchList[0].id;
        }
      });
    },

    loadInternsByBatch() {
      console.log("loading...");
      this.skillsList = [];
      this.skillRequired = false;
      this.studentList = [];
      this.selectedStudents = [];
      this.loadSkills();
    },

    initialize() {
      this.axiosCall("/organization/getMyProgramsTied", "GET").then((res) => {
        if (res) this.programsList = res.data;
      });
    },

    getProgramAbbr(id) {
      if (id) {
        const data = this.programsList.find((item) => item.id == id);
        return data ? data.abbr : "";
      }
      return "";
    },

    getStudentById(id) {
      return this.studentList.find((s) => s.id === id);
    },

    getStudentName(id) {
      const s = this.getStudentById(id);
      return s ? `${s.fname} ${s.lname}` : "";
    },

    getStudentInitials(id) {
      const s = this.getStudentById(id);
      if (!s) return "?";
      return `${s.fname?.[0] ?? ""}${s.lname?.[0] ?? ""}`.toUpperCase();
    },

    getStudentProgram(id) {
      const s = this.getStudentById(id);
      return s ? this.getProgramAbbr(s.program_id) : "";
    },

    removeStudent(id) {
      this.selectedStudents = this.selectedStudents.filter((sid) => sid !== id);
    },

    async save() {
      let validate = await this.$refs.InternRequestForm.validate();
      if (validate.valid) {
        if (this.programSelected.length == 0) {
          this.fadeAwayMessage.show = true;
          this.fadeAwayMessage.type = "error";
          this.fadeAwayMessage.header = "System Message";
          this.fadeAwayMessage.message = "Please select a program.";
          return;
        }
        let data = {
          programSelected: JSON.stringify(this.programSelected),
          num_interns: this.num_interns,
          skills: JSON.stringify(
            this.requestMode === "skills" ? this.selectedSkills : [],
          ),
          student_requested: JSON.stringify(
            this.requestMode === "specific" ? this.selectedStudents : [],
          ),
          student_batch_id: this.batch_selected,
        };
        this.axiosCall("/organization/add-intern-request", "POST", data).then(
          (res) => {
            if (res.data.status == 201) {
              this.fadeAwayMessage.show = true;
              this.fadeAwayMessage.type = "success";
              this.fadeAwayMessage.header = "System Message";
              this.fadeAwayMessage.message = res.data.msg;
              this.closeD();
            } else if (res.data.status == 400) {
              this.fadeAwayMessage.show = true;
              this.fadeAwayMessage.type = "error";
              this.fadeAwayMessage.header = "System Message";
              this.fadeAwayMessage.message = res.data.msg;
            }
          },
        );
      }
    },

    closeD() {
      eventBus.emit("closeCompanyInternRequestDialog", true);
      this.btnLoading = false;
      this.dialog = false;
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap");

/* ── Card ── */
.dialog-card {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  border-radius: 16px !important;
  overflow: hidden;
}

/* ── Header ── */
.dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px;
}
.dialog-header-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #dcfce7;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.dialog-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}
.dialog-subtitle {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 1px;
}
.close-btn {
  color: #9ca3af !important;
}

/* ── Body ── */
.dialog-body {
  padding: 20px !important;
  max-height: 560px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
}

/* ── Section ── */
.form-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.section-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.section-num {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #16a34a;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.section-badge {
  margin-left: auto;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  padding: 1px 9px;
}

/* ── Field ── */
.field-input :deep(.v-field) {
  border-radius: 8px !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
}
.field-input :deep(.v-field__outline__start),
.field-input :deep(.v-field__outline__end),
.field-input :deep(.v-field__outline__notch) {
  border-color: #d1d5db !important;
}
.field-input :deep(.v-field--focused .v-field__outline__start),
.field-input :deep(.v-field--focused .v-field__outline__end),
.field-input :deep(.v-field--focused .v-field__outline__notch) {
  border-color: #16a34a !important;
}
.field-input :deep(input),
.field-input :deep(.v-field__input) {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  color: #111827 !important;
}

/* ── Programs grid ── */
.programs-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.program-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  border-radius: 8px;
  border: 1.5px solid #e5e7eb;
  background: #f9fafb;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
}
.program-chip:hover {
  border-color: #16a34a;
  color: #16a34a;
  background: #f0fdf4;
}
.program-chip.active {
  border-color: #16a34a;
  background: #dcfce7;
  color: #15803d;
}
.chip-check {
  color: #16a34a;
}

/* ── Mode toggle ── */
.mode-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.mode-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  border-radius: 10px;
  border: 1.5px solid #e5e7eb;
  background: #f9fafb;
  cursor: pointer;
  transition: all 0.15s ease;
}
.mode-option:hover {
  border-color: #16a34a;
  background: #f0fdf4;
}
.mode-option.active {
  border-color: #16a34a;
  background: #dcfce7;
}
.mode-icon-wrap {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #6b7280;
  transition: all 0.15s ease;
}
.mode-icon-wrap.active {
  background: #16a34a;
  color: #fff;
}
.mode-title {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
}
.mode-desc {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
  line-height: 1.4;
}

/* ── Skills grid ── */
.skills-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.skill-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1.5px solid #e5e7eb;
  background: #f9fafb;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
}
.skill-chip:hover {
  border-color: #2563eb;
  color: #1d4ed8;
  background: #eff6ff;
}
.skill-chip.active {
  border-color: #2563eb;
  background: #dbeafe;
  color: #1d4ed8;
}
.skill-check {
  color: #2563eb;
}

/* ── Student chip in autocomplete ── */
.student-chip {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 11px !important;
}

/* ── Selected preview ── */
.selected-preview {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
  max-height: 100px;
  overflow-y: auto;
}
.selected-student-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.student-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #dcfce7;
  color: #15803d;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.student-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.student-name {
  font-size: 12px;
  font-weight: 600;
  color: #111827;
}
.student-prog {
  font-size: 11px;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 4px;
  padding: 0 6px;
  display: inline-block;
  width: fit-content;
}
.remove-btn {
  color: #9ca3af !important;
}

/* ── Footer ── */
.dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
}
.footer-cancel-btn {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  background: #f3f4f6 !important;
  color: #374151 !important;
  border: 1px solid #e5e7eb !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 38px !important;
  padding: 0 18px !important;
}
.footer-submit-btn {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  border-radius: 8px !important;
  background: #16a34a !important;
  color: #fff !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 38px !important;
  padding: 0 20px !important;
}
</style>
